import { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { useLocation, useNavigate } from 'react-router-dom'
import { PageTitle } from 'shared/components/layout/PageTitle'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Label } from 'shared/ui/label'
import { Input } from 'shared/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { Switch } from 'shared/ui/switch'
import { Button } from 'shared/ui/button'
import { Filter, Search, RotateCcw, Plus } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'shared/ui/dropdown-menu'
import { PermitIndex, getPermits, getPermitStatusOptions, type PermitListItem } from 'modules/permit'
import { DatePicker } from 'modules/permit/components/DatePicker'
import { getContractorCompanies, type ContractorCompany } from '../api/contractorService'
import { fetchWorkAreas, type WorkAreaOption } from 'modules/admin/api/plotPlanService'
import { getAvailablePermitTypes } from 'shared/api/templateService'
import { getPersistedSelectedSiteId, resolveTenantCandidate, resolveTenantId } from 'shared/utils/tenantContext'
import {
  FILTER_STATUS_CODES,
  getPermitStatusLabel,
  normalizePermitStatus,
  PERMIT_STATUS_FILTER_OPTIONS,
} from 'modules/permit/constants/permitStatus'

interface LayoutState {
  user?: any
  module?: 'E-PTW' | 'ADMIN'
  site?: {
    id?: string
    name?: string
    location?: string
    description?: string
    activeUsers?: number
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DEFAULT_USER = {
  name: 'ผู้ใช้งาน',
  role: 'Permit Requester',
  department: 'Maintenance Department',
}

// Deprecated legacy filter list kept temporarily for reference; UI uses ACTIVE_STATUS_FILTERS below.
const LEGACY_STATUS_FILTERS = [
  { value: 'ALL', label: 'ทั้งหมด' },
  { value: 'P0001', label: 'ร่าง' },
  { value: 'P0010', label: 'สร้างใหม่' },
  { value: 'P0015', label: 'รอการแก้ไขจากผู้สร้าง' },
  { value: 'P0251', label: 'รอผู้ขอให้ข้อมูลเพิ่มเติม' },
  { value: 'P0020', label: 'รอผู้ควบคุมงานตรวจสอบ' },
  { value: 'P0030', label: 'รอหัวหน้ากะตรวจสอบ' },
  { value: 'P0040', label: 'รอ Shift Manager อนุมัติ' },
  { value: 'P0050', label: 'รอผู้จัดการอนุมัติ' },
  { value: 'P0060', label: 'รอผู้จัดการฝ่ายอนุมัติ' },
  { value: 'P0070', label: 'รอเจ้าของพื้นที่รับ-จ่ายงาน' },
  { value: 'P0080', label: 'รอการเปิด Permit' },
  { value: 'P0008', label: 'รอเปิดงาน (ปัจจุบัน)' },
  { value: 'P0009', label: 'เปิด Permit' },
  { value: 'P9000', label: 'ปิด Permit' },
  { value: 'P9998', label: 'ยกเลิก' },
]
const ACTIVE_STATUS_FILTERS = [{ value: 'ALL', label: 'ทั้งหมด' }, ...PERMIT_STATUS_FILTER_OPTIONS]
const PAGE_SIZE_OPTIONS = ['10', '20', '50']
const createDefaultFilters = (showMineOnly = true) => ({
  keyword: '',
  statuses: [] as string[],
  permitTypes: [] as string[],
  companyIds: [] as string[],
  workAreaIds: [] as string[],
  dateFrom: '',
  dateTo: '',
  showMineOnly,
})

const GUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

const normalizeGuid = (value?: string | null) => {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  return GUID_REGEX.test(trimmed) ? trimmed.toLowerCase() : null
}

const normalizeUserType = (value?: string | null) => {
  if (typeof value !== 'string') {
    return ''
  }
  return value.trim().toUpperCase()
}

const looksLikePermitNumber = (value?: string | null) => {
  if (typeof value !== 'string') {
    return false
  }

  const trimmed = value.trim()
  if (trimmed.length < 6 || trimmed.includes(' ')) {
    return false
  }

  return /^[A-Za-z]{1,4}\d{4,}[A-Za-z0-9-]*$/.test(trimmed)
}


export function PermitIndexPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const state = (location.state as LayoutState | undefined) ?? {}
  const persistedSiteId = useMemo(() => getPersistedSelectedSiteId(), [])

  const [filters, setFilters] = useState(createDefaultFilters)
  const [appliedFilters, setAppliedFilters] = useState(createDefaultFilters)
  const [permits, setPermits] = useState<PermitListItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [companyOptions, setCompanyOptions] = useState<ContractorCompany[]>([])
  const [companySearchTerm, setCompanySearchTerm] = useState('')
  const [isCompanyLoading, setIsCompanyLoading] = useState(false)
  const [workAreaOptions, setWorkAreaOptions] = useState<WorkAreaOption[]>([])
  const [workAreaSearchTerm, setWorkAreaSearchTerm] = useState('')
  const [isWorkAreaLoading, setIsWorkAreaLoading] = useState(false)
  const [permitTypeOptions, setPermitTypeOptions] = useState<{ value: string; label: string }[]>([
    { value: 'ALL', label: 'ทั้งหมด' },
  ])
  const [statusFilterOptions, setStatusFilterOptions] = useState(ACTIVE_STATUS_FILTERS)

  const tenantId = resolveTenantId(state.site?.id, persistedSiteId)
  const siteId = resolveTenantCandidate(state.site?.id, persistedSiteId)
  const currentUserType = normalizeUserType(
    state.user?.userType ?? state.user?.user_type ?? state.user?.type,
  )
  const isContractorUser = currentUserType === 'CONTRACTOR'
  const contractorCompanyId = useMemo(() => {
    const candidates: Array<string | undefined | null> = [
      state.user?.companyId,
      state.user?.company_id,
      state.user?.company?.companyId,
      state.user?.company?.id,
    ]

    for (const candidate of candidates) {
      const normalized = normalizeGuid(candidate)
      if (normalized) {
        return normalized
      }
    }

    return null
  }, [state.user])

  const requesterId = useMemo(() => {
    const candidates: Array<string | undefined | null> = [
      state.user?.id,
      state.user?.user_id,
      state.user?.userId,
      state.user?.employee_id,
      state.user?.employeeId,
      state.user?.emp_id,
      state.user?.empId,
    ]

    for (const candidate of candidates) {
      const normalized = normalizeGuid(candidate)
      if (normalized) {
        return normalized
      }
    }

    return null
  }, [state.user])

  const currentUserName = useMemo(() => {
    const candidates: Array<string | undefined | null> = [
      state.user?.fullName,
      state.user?.name,
      state.user?.username,
      state.user?.userName,
    ]

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim()
      }
    }

    return null
  }, [state.user])

  const contractorCompanyLabel = useMemo(() => {
    const matched = companyOptions.find(
      (company) => normalizeGuid(company.companyId) === contractorCompanyId,
    )
    if (matched?.companyName) {
      return matched.companyName
    }

    const fallbackCandidates: Array<string | undefined | null> = [
      state.user?.companyName,
      state.user?.company_name,
      state.user?.company,
      state.user?.requesterCompany,
    ]

    for (const candidate of fallbackCandidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim()
      }
    }

    return contractorCompanyId ?? '-'
  }, [companyOptions, contractorCompanyId, state.user])

  const lockedCompanyIds = useMemo(
    () => (isContractorUser && contractorCompanyId ? [contractorCompanyId] : []),
    [contractorCompanyId, isContractorUser],
  )
  const buildDefaultFilters = useCallback(
    () => createDefaultFilters(!isContractorUser),
    [isContractorUser],
  )

  const applyCompanyLock = useCallback(
    (input: ReturnType<typeof createDefaultFilters>) => {
      if (!isContractorUser) {
        return input
      }
      return {
        ...input,
        companyIds: lockedCompanyIds,
        showMineOnly: isContractorUser ? false : input.showMineOnly,
      }
    },
    [isContractorUser, lockedCompanyIds],
  )

  const filteredCompanies = useMemo(() => {
    const term = companySearchTerm.trim().toLowerCase()
    const source = isContractorUser
      ? companyOptions.filter((company) => normalizeGuid(company.companyId) === contractorCompanyId)
      : companyOptions
    if (!term) {
      return source
    }
    return source.filter((company) => {
      const name = company.companyName?.toLowerCase() ?? ''
      const tax = company.taxId?.toLowerCase() ?? ''
      return name.includes(term) || tax.includes(term)
    })
  }, [companyOptions, companySearchTerm, contractorCompanyId, isContractorUser])
  const filteredWorkAreas = useMemo(() => {
    const term = workAreaSearchTerm.trim().toLowerCase()
    if (!term) {
      return workAreaOptions
    }
    return workAreaOptions.filter((item) => {
      const name = item.name?.toLowerCase() ?? ''
      const code = item.code?.toLowerCase() ?? ''
      return name.includes(term) || code.includes(term)
    })
  }, [workAreaOptions, workAreaSearchTerm])

  useEffect(() => {
    if (!state.user) {
      navigate('/modules', { replace: true })
    }
  }, [state.user, navigate])

  useEffect(() => {
    if (!isContractorUser) {
      return
    }

    setFilters((prev) => {
      if (
        prev.companyIds.length === lockedCompanyIds.length &&
        prev.companyIds.every((id, index) => id === lockedCompanyIds[index])
      ) {
        return prev
      }
      return { ...prev, companyIds: lockedCompanyIds, showMineOnly: false }
    })

    setAppliedFilters((prev) => {
      if (
        prev.companyIds.length === lockedCompanyIds.length &&
        prev.companyIds.every((id, index) => id === lockedCompanyIds[index])
      ) {
        return prev
      }
      return { ...prev, companyIds: lockedCompanyIds, showMineOnly: false }
    })
  }, [isContractorUser, lockedCompanyIds])

  useEffect(() => {
    let cancelled = false
    setIsCompanyLoading(true)
    getContractorCompanies({ tenantId, pageNumber: 1, pageSize: 1000 })
      .then((result) => {
        if (cancelled) return
        setCompanyOptions(result.items ?? [])
      })
      .catch((error) => {
        console.error('ไม่สามารถโหลดรายชื่อบริษัทได้', error)
        if (!cancelled) {
          setCompanyOptions([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsCompanyLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [tenantId])

  useEffect(() => {
    let cancelled = false
    getAvailablePermitTypes(tenantId)
      .then((items) => {
        if (cancelled) return
        const options =
          items?.map((item) => ({
            value: item.permitCode || item.permitType || '',
            label: item.permitType || item.permitCode || '',
          })) ?? []
        setPermitTypeOptions([{ value: 'ALL', label: 'ทั้งหมด' }, ...options.filter((opt) => opt.value)])
      })
      .catch(() => {
        if (cancelled) return
        setPermitTypeOptions([
          { value: 'ALL', label: 'ทั้งหมด' },
          { value: 'Hot Work', label: 'Hot Work' },
          { value: 'Cold Work', label: 'Cold Work' },
          { value: 'Confine Work Space', label: 'Confine Work Space' },
          { value: 'Excavation', label: 'Excavation' },
        ])
      })

    return () => {
      cancelled = true
    }
  }, [tenantId])

  useEffect(() => {
    let cancelled = false
    getPermitStatusOptions(tenantId)
      .then((options) => {
        if (cancelled) return
        const descrByCode = new Map(options.map((option) => [option.code.toUpperCase(), option.descr]))
        const merged = FILTER_STATUS_CODES.map((code) => ({
          value: code as string,
          label: descrByCode.get(code.toUpperCase()) || getPermitStatusLabel(code),
        }))
        setStatusFilterOptions([{ value: 'ALL', label: 'ทั้งหมด' }, ...merged])
      })
      .catch((error) => {
        console.error('ไม่สามารถโหลดรายการสถานะเอกสารได้', error)
      })

    return () => {
      cancelled = true
    }
  }, [tenantId])

  useEffect(() => {
    let cancelled = false
    setIsWorkAreaLoading(true)
    fetchWorkAreas(tenantId)
      .then((items) => {
        if (cancelled) return
        setWorkAreaOptions(items ?? [])
      })
      .catch((error) => {
        console.error('ไม่สามารถโหลดพื้นที่ปฏิบัติงานได้', error)
        if (!cancelled) {
          setWorkAreaOptions([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsWorkAreaLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [tenantId])

  // Keep API fetching close to the page (similar to PermitRequestPage) so that the view is only a presenter.
  const loadPermits = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    const keyword = appliedFilters.keyword?.trim() || ''
    const permitNumberKeyword = looksLikePermitNumber(keyword) ? keyword : undefined
    const searchKeyword = permitNumberKeyword ? undefined : keyword || undefined
    const normalizedStatuses = Array.from(
      new Set(
        (appliedFilters.statuses ?? [])
          .map((status) => status?.trim())
          .filter((status): status is string => Boolean(status) && status.toUpperCase() !== 'ALL')
          .map((status) => normalizePermitStatus(status) ?? status.toUpperCase()),
      ),
    )
    const normalizedPermitTypes = (appliedFilters.permitTypes ?? [])
      .map((type) => type?.trim())
      .filter((type) => type && type.toUpperCase() !== 'ALL')
    const companyIds = applyCompanyLock({
      ...buildDefaultFilters(),
      ...appliedFilters,
    }).companyIds.filter((id) => id)
    const workAreaIds = (appliedFilters.workAreaIds ?? []).filter((id) => id)
    if (isContractorUser && companyIds.length === 0) {
      setLoadError('ไม่พบบริษัทของผู้ใช้งาน contractor จึงไม่สามารถค้นหาเอกสารได้')
      setPermits([])
      setTotalItems(0)
      setTotalPages(1)
      setIsLoading(false)
      return
    }
    try {
      const response = await getPermits({
        tenantId,
        requesterId: appliedFilters.showMineOnly ? requesterId : undefined,
        siteId,
        companyIds: companyIds.length > 0 ? companyIds : undefined,
        locationIds: workAreaIds.length > 0 ? workAreaIds : undefined,
        statuses: normalizedStatuses.length > 0 ? normalizedStatuses : undefined,
        permitTypes: normalizedPermitTypes.length > 0 ? normalizedPermitTypes : undefined,
        permitNumber: permitNumberKeyword,
        searchTerm: searchKeyword,
        plannedStartFrom: appliedFilters.dateFrom || undefined,
        plannedStartTo: appliedFilters.dateTo || undefined,
        pageNumber,
        pageSize,
      })

      setPermits(response.items ?? [])

      const safeTotalPages = Math.max(response.totalPages ?? 1, 1)
      setTotalItems(response.totalItems ?? response.items?.length ?? 0)
      setTotalPages(safeTotalPages)

      if (pageNumber > safeTotalPages) {
        setPageNumber(safeTotalPages)
      }
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? (error.response?.data as { message?: string } | undefined)?.message ??
        error.response?.statusText ??
        error.message
        : 'ไม่สามารถโหลดข้อมูล Permit ได้'
      setLoadError(message || 'ไม่สามารถโหลดข้อมูล Permit ได้')
      setPermits([])
      setTotalItems(0)
      setTotalPages(1)
    } finally {
      setIsLoading(false)
    }
  }, [appliedFilters, applyCompanyLock, buildDefaultFilters, isContractorUser, pageNumber, pageSize, requesterId, siteId, tenantId])

  useEffect(() => {
    loadPermits()
  }, [loadPermits])

  const handleCreateNew = () => {
    navigate('/permit-request', {
      state: {
        ...state,
        mode: 'create',
        permitId: undefined,
        entryMode: 'create',
        hideActionBar: false,
        forceReadOnly: false,
      },
    })
  }

  const handleViewPermit = (permitId: string) => {
    navigate('/permit-request', {
      state: {
        ...state,
        mode: 'edit',
        permitId,
        forceReadOnly: true,
        hideActionBar: true,
        entryMode: 'display',
      },
    })
  }

  const handleEditPermit = (permitId: string) => {
    navigate('/permit-request', {
      state: {
        ...state,
        mode: 'edit',
        permitId,
        entryMode: 'edit',
        hideActionBar: false,
        forceReadOnly: false,
      },
    })
  }

  const handleSearch = () => {
    setPageNumber(1)
    setAppliedFilters(applyCompanyLock(filters))
  }

  const handleClearFilters = () => {
    const reset = applyCompanyLock(buildDefaultFilters())
    setFilters(reset)
    setAppliedFilters(reset)
    setPageSize(10)
    setPageNumber(1)
  }

  const handlePageChange = (nextPage: number) => {
    if (nextPage >= 1 && nextPage <= totalPages) {
      setPageNumber(nextPage)
    }
  }

  const handlePageSizeChange = (value: string) => {
    const nextSize = Number(value) || 10
    setPageSize(nextSize)
    setPageNumber(1)
  }

  const toggleValue = (list: string[], value: string, checked: boolean) => {
    if (checked) {
      if (list.includes(value)) return list
      return [...list, value]
    }
    return list.filter((item) => item !== value)
  }

  const selectedCompanies = useMemo(() => {
    if (isContractorUser) {
      if (!contractorCompanyId) {
        return []
      }
      return [
        {
          companyId: contractorCompanyId,
          companyName: contractorCompanyLabel,
          isActive: true,
        } as ContractorCompany,
      ]
    }
    return companyOptions.filter((company) => filters.companyIds.includes(company.companyId))
  }, [companyOptions, contractorCompanyId, contractorCompanyLabel, filters.companyIds, isContractorUser])

  const selectedWorkAreas = useMemo(
    () => workAreaOptions.filter((item) => filters.workAreaIds.includes(item.locationId)),
    [filters.workAreaIds, workAreaOptions],
  )

  const renderSelectedSummary = (items: { label: string }[]) => {
    if (items.length === 0) return 'ทั้งหมด'
    if (items.length === 1) return items[0].label
    return `${items[0].label} +${items.length - 1}`
  }

  const handleNavigate = (page: string) => {
    if (page === 'home') {
      navigate('/dashboard', { state })
    }
  }

  const startIndex = totalItems === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const endIndex = Math.min(pageNumber * pageSize, totalItems)

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1">
          <PageTitle currentPage="permit-index" onNavigate={handleNavigate} compact />
        </div>
        <Button
          onClick={handleCreateNew}
          className="self-start rounded-full bg-emerald-600 px-5 text-white shadow-sm hover:bg-emerald-700"
        >
          <Plus className="mr-2 h-4 w-4" />
          สร้าง Permit ใหม่
        </Button>
      </div>

      <div className="space-y-6">
        <Card className="overflow-hidden border border-blue-200 shadow-sm">
          <CardHeader className="border-b border-blue-100 bg-blue-50/70 p-3">
            <div className="flex flex-col gap-1">
              <CardTitle className="flex items-center gap-2 text-lg text-slate-800">
                <Filter className="h-5 w-5 text-blue-600" />
                เงื่อนไขการค้นหา
              </CardTitle>
              <p className="text-sm text-slate-600">
                กรอกข้อมูลตามเงื่อนไขต่าง ๆ เพื่อช่วยค้นหาและจัดเรียงข้อมูลได้ง่ายขึ้น
              </p>
            </div>
          </CardHeader>
          <CardContent className="bg-blue-50/50 p-3">
            <form
              onSubmit={(event) => {
                event.preventDefault()
                handleSearch()
              }}
              className="space-y-5"
            >
              <div className="grid gap-4 lg:grid-cols-12">
                <div className="lg:col-span-3">
                  <Label htmlFor="keyword">คำค้นหา</Label>
                  <div className="relative mt-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      id="keyword"
                      placeholder="เลขที่หรือรายละเอียดงาน"
                      value={filters.keyword}
                      onChange={(event) =>
                        setFilters((prev) => ({ ...prev, keyword: event.target.value }))
                      }
                      className="bg-white pl-9"
                    />
                  </div>
                </div>
                <div className="lg:col-span-2">
                  <Label htmlFor="status">สถานะเอกสาร</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="mt-1 w-full justify-between bg-white">
                        <span>
                          {renderSelectedSummary(
                            (filters.statuses.length === 0
                              ? []
                              : statusFilterOptions.filter((s) => filters.statuses.includes(s.value))
                            ).map((s) => ({ label: s.label })),
                          )}
                        </span>
                        <span className="text-xs text-slate-500">เลือกได้หลายค่า</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56 max-h-80 overflow-y-auto">
                      <DropdownMenuLabel>สถานะเอกสาร</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuCheckboxItem
                        checked={filters.statuses.length === 0}
                        onCheckedChange={() => setFilters((prev) => ({ ...prev, statuses: [] }))}
                        onSelect={(event) => event.preventDefault()}
                      >
                        ทั้งหมด
                      </DropdownMenuCheckboxItem>
                      {statusFilterOptions.filter((status) => status.value !== 'ALL').map((status) => (
                        <DropdownMenuCheckboxItem
                          key={status.value}
                          checked={filters.statuses.includes(status.value)}
                          onCheckedChange={(checked) =>
                            setFilters((prev) => ({
                              ...prev,
                              statuses: toggleValue(prev.statuses, status.value, checked === true),
                            }))
                          }
                          onSelect={(event) => event.preventDefault()}
                        >
                          {status.label}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="lg:col-span-2">
                  <Label htmlFor="company">ชื่อบริษัท</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="mt-1 w-full justify-between bg-white"
                        disabled={isContractorUser}
                      >
                        <span>
                          {renderSelectedSummary(
                            selectedCompanies.map((company) => ({ label: company.companyName })),
                          )}
                        </span>
                        <span className="text-xs text-slate-500">
                          {isContractorUser
                            ? contractorCompanyId
                              ? '1 บริษัท'
                              : 'ไม่พบบริษัท'
                            : filters.companyIds.length === 0
                              ? 'ทั้งหมด'
                              : `${filters.companyIds.length} บริษัท`}
                        </span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-64 max-h-80 overflow-y-auto">
                      <div className="px-3 py-2">
                        <Input
                          placeholder="ค้นหาบริษัท..."
                          value={companySearchTerm}
                          onChange={(event) => setCompanySearchTerm(event.target.value)}
                          onKeyDown={(event) => event.stopPropagation()}
                          className="h-8"
                        />
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuCheckboxItem
                        checked={filters.companyIds.length === 0}
                        onCheckedChange={() => setFilters((prev) => ({ ...prev, companyIds: [] }))}
                        onSelect={(event) => event.preventDefault()}
                      >
                        ทั้งหมด
                      </DropdownMenuCheckboxItem>
                      {isCompanyLoading ? (
                        <DropdownMenuCheckboxItem disabled checked={false}>
                          กำลังโหลด...
                        </DropdownMenuCheckboxItem>
                      ) : null}
                      {!isCompanyLoading && filteredCompanies.length === 0 ? (
                        <DropdownMenuCheckboxItem disabled checked={false}>
                          ไม่พบบริษัท
                        </DropdownMenuCheckboxItem>
                      ) : null}
                      {filteredCompanies.map((company) => (
                        <DropdownMenuCheckboxItem
                          key={company.companyId}
                          checked={filters.companyIds.includes(company.companyId)}
                          onCheckedChange={(checked) =>
                            setFilters((prev) => ({
                              ...prev,
                              companyIds: toggleValue(prev.companyIds, company.companyId, checked === true),
                            }))
                          }
                          onSelect={(event) => event.preventDefault()}
                        >
                          {company.companyName}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="lg:col-span-2">
                  <Label htmlFor="permitType">ประเภท Permit</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="mt-1 w-full justify-between bg-white">
                        <span>
                          {renderSelectedSummary(
                            (filters.permitTypes.length === 0
                              ? []
                              : permitTypeOptions.filter((p) => filters.permitTypes.includes(p.value))
                            ).map((p) => ({ label: p.label })),
                          )}
                        </span>
                        <span className="text-xs text-slate-500">เลือกได้หลายค่า</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56 max-h-80 overflow-y-auto">
                      <DropdownMenuLabel>ประเภท Permit</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuCheckboxItem
                        checked={filters.permitTypes.length === 0}
                        onCheckedChange={() => setFilters((prev) => ({ ...prev, permitTypes: [] }))}
                        onSelect={(event) => event.preventDefault()}
                      >
                        ทั้งหมด
                      </DropdownMenuCheckboxItem>
                      {permitTypeOptions
                        .filter((type) => type.value.toUpperCase() !== 'ALL')
                        .map((type) => (
                        <DropdownMenuCheckboxItem
                          key={type.value}
                          checked={filters.permitTypes.includes(type.value)}
                          onCheckedChange={(checked) =>
                            setFilters((prev) => ({
                              ...prev,
                              permitTypes: toggleValue(prev.permitTypes, type.value, checked === true),
                            }))
                          }
                          onSelect={(event) => event.preventDefault()}
                        >
                          {type.label}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="lg:col-span-3">
                  <Label htmlFor="workArea">พื้นที่ปฏิบัติงาน</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="mt-1 w-full justify-between bg-white">
                        <span>
                          {renderSelectedSummary(
                            selectedWorkAreas.map((item) => ({ label: item.name || item.code || item.locationId })),
                          )}
                        </span>
                        <span className="text-xs text-slate-500">
                          {filters.workAreaIds.length === 0 ? 'ทั้งหมด' : `${filters.workAreaIds.length} พื้นที่`}
                        </span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-64 max-h-80 overflow-y-auto">
                      <div className="px-3 py-2">
                        <Input
                          placeholder="ค้นหาพื้นที่..."
                          value={workAreaSearchTerm}
                          onChange={(event) => setWorkAreaSearchTerm(event.target.value)}
                          onKeyDown={(event) => event.stopPropagation()}
                          className="h-8"
                        />
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuCheckboxItem
                        checked={filters.workAreaIds.length === 0}
                        onCheckedChange={() => setFilters((prev) => ({ ...prev, workAreaIds: [] }))}
                        onSelect={(event) => event.preventDefault()}
                      >
                        ทั้งหมด
                      </DropdownMenuCheckboxItem>
                      {isWorkAreaLoading ? (
                        <DropdownMenuCheckboxItem disabled checked={false}>
                          กำลังโหลด...
                        </DropdownMenuCheckboxItem>
                      ) : null}
                      {!isWorkAreaLoading && filteredWorkAreas.length === 0 ? (
                        <DropdownMenuCheckboxItem disabled checked={false}>
                          ไม่พบพื้นที่ปฏิบัติงาน
                        </DropdownMenuCheckboxItem>
                      ) : null}
                      {filteredWorkAreas.map((item) => (
                        <DropdownMenuCheckboxItem
                          key={item.locationId}
                          checked={filters.workAreaIds.includes(item.locationId)}
                          onCheckedChange={(checked) =>
                            setFilters((prev) => ({
                              ...prev,
                              workAreaIds: toggleValue(prev.workAreaIds, item.locationId, checked === true),
                            }))
                          }
                          onSelect={(event) => event.preventDefault()}
                        >
                          {item.name || item.code}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-12">
                <div className="lg:col-span-3">
                  <Label htmlFor="dateFrom">วันที่เริ่มต้น</Label>
                  <DatePicker
                    id="dateFrom"
                    value={filters.dateFrom || ''}
                    onChange={(date) => setFilters((prev) => ({ ...prev, dateFrom: date }))}
                    placeholder="เลือกวันที่"
                    className="mt-1"
                    allowPastDates 
                  />
                </div>
                <div className="lg:col-span-3">
                  <Label htmlFor="dateTo">วันที่สิ้นสุด</Label>
                  <DatePicker
                    id="dateTo"
                    value={filters.dateTo || ''}
                    onChange={(date) => setFilters((prev) => ({ ...prev, dateTo: date }))}
                    placeholder="เลือกวันที่"
                    className="mt-1"
                    allowPastDates
                  />
                </div>
                <div className="lg:col-span-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-end">
                  <div className="flex-1">
                    <div className="flex h-full items-start gap-3 rounded-lg border border-blue-100 bg-white/70 p-3 shadow-inner">
                      <Switch
                        id="mine-only"
                        checked={filters.showMineOnly}
                        disabled={isContractorUser}
                        onCheckedChange={(checked) =>
                          setFilters((prev) => ({ ...prev, showMineOnly: checked === true }))
                        }
                      />
                      <div>
                        <Label htmlFor="mine-only" className="text-sm text-slate-800">
                          แสดงเฉพาะงานของฉัน
                        </Label>
                        <p className="text-xs text-blue-600">
                          ระบบจะแสดงเฉพาะงานที่คุณเป็นผู้ขออนุญาตหรือผู้ควบคุมงาน
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-3">
                    <Button type="button" variant="outline" onClick={handleClearFilters}>
                      <RotateCcw className="mr-2 h-4 w-4" />
                      ล้างข้อมูล
                    </Button>
                    <Button type="submit" className="bg-blue-600 text-white hover:bg-blue-700">
                      <Search className="mr-2 h-4 w-4" />
                      ค้นหา
                    </Button>
                  </div>
                </div>
              </div>

              {/* <Alert className="border-blue-100 bg-white/80 text-slate-700">
                <AlertCircle className="h-4 w-4 text-blue-500" />
                <AlertDescription>
                  ระบบจะโหลดข้อมูลใหม่ทุกครั้งเมื่อกดค้นหา เพื่อให้ผลลัพธ์ตรงกับเงื่อนไขล่าสุด
                </AlertDescription>
              </Alert> */}
            </form>
          </CardContent>
        </Card>

        <PermitIndex
          permits={permits}
          isLoading={isLoading}
          error={loadError}
          onViewPermit={handleViewPermit}
          onEditPermit={handleEditPermit}
          currentUserType={currentUserType}
          currentUserId={requesterId}
          currentUserName={currentUserName}
          tenantId={tenantId}
          locationLabelMap={workAreaOptions.reduce<Record<string, string>>((acc, item) => {
            if (item.locationId) {
              acc[item.locationId] = item.name || item.code || item.locationId
            }
            if (item.code) {
              acc[item.code] = item.name || item.code
            }
            return acc
          }, {})}
        />

        <Card className="border-amber-200 p-3">
          <CardContent className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between p-0">
            <div className="text-sm text-gray-600">
              แสดง {startIndex === 0 ? 0 : startIndex} - {endIndex} จากทั้งหมด {totalItems} รายการ
            </div>
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>จำนวนต่อหน้า</span>
                <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="h-9 w-24 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={size}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageNumber <= 1}
                  onClick={() => handlePageChange(pageNumber - 1)}
                >
                  ก่อนหน้า
                </Button>
                <span className="text-sm text-gray-600">
                  หน้า {pageNumber} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageNumber >= totalPages}
                  onClick={() => handlePageChange(pageNumber + 1)}
                >
                  ถัดไป
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

